"use client";

import { useEffect, useId, useRef, useState } from "react";

import { CarouselCentered } from "@/components/carouselCentered/carouselCentered.component";
import { ReasonsToBuyItem } from "@/components/reasonsToBuySection/reasonsToBuyItem.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { MonitoringPlanCard } from "@/app/blu/monitoring/components/monitoringPlanCard/monitoringPlanCard.component";
import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import type { RichText as RichTextType } from "@/sanity.types";

import {
  BUILD_PROTECTION_SECTION_ID,
  BUILD_PROTECTION_SURFACE_SHADOW,
  BUILD_PROTECTION_TAB_PARAM,
  type BuildProtectionTabId,
  isBuildProtectionTabId,
} from "./buildProtection.constants";
import type {
  BuildProtectionBlock,
  BuildProtectionProductItem,
} from "./buildProtection.types";
import { BuildProtectionCta } from "./buildProtectionCta.component";
import { BuildProtectionProductCard } from "./buildProtectionProductCard.component";
import {
  BuildProtectionLegal,
  BuildProtectionSystemOverview,
} from "./buildProtectionSystemOverview.component";

export function BuildProtection({ block }: { block: BuildProtectionBlock }) {
  const tabs = availableTabs(block);
  const defaultTab = resolveDefaultTab(block.defaultTab, tabs);
  const [selected, setSelected] = useState(defaultTab);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabListId = useId();
  const sectionId = block.sectionId || BUILD_PROTECTION_SECTION_ID;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash.replace(/^#/, "");
    const hashQuery = hash.includes("?")
      ? hash.slice(hash.indexOf("?") + 1)
      : "";
    const requested =
      params.get(BUILD_PROTECTION_TAB_PARAM) ??
      new URLSearchParams(hashQuery).get(BUILD_PROTECTION_TAB_PARAM);
    const heroOpensSystem =
      hash === sectionId || hash.startsWith(`${sectionId}?`);

    if (
      isBuildProtectionTabId(requested) &&
      tabs.some((tab) => tab.id === requested)
    ) {
      setSelected(requested);
      return;
    }

    if (heroOpensSystem && tabs.some((tab) => tab.id === "buildMySystem")) {
      setSelected("buildMySystem");
    }
  }, []);

  useEffect(() => {
    if (!selected) return;
    const node = tabRefs.current[selected];
    const list = node?.parentElement;
    if (!node || !list || typeof list.scrollTo !== "function") return;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior = reduceMotion ? "auto" : "smooth";
    const focusPad = 4;
    const leftEdge = node.offsetLeft - focusPad;
    const rightEdge = node.offsetLeft + node.offsetWidth + focusPad;
    if (leftEdge < list.scrollLeft) {
      list.scrollTo({ left: leftEdge, behavior });
    } else if (rightEdge > list.scrollLeft + list.clientWidth) {
      list.scrollTo({ left: rightEdge - list.clientWidth, behavior });
    }
  }, [selected]);

  if (!tabs.length || !selected) return null;

  const onTabsKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = tabs.findIndex((tab) => tab.id === selected);
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft")
      next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = tabs.length - 1;
    else return;

    event.preventDefault();
    const nextId = tabs[next].id;
    setSelected(nextId);
    tabRefs.current[nextId]?.focus();
  };

  return (
    <section
      id={sectionId}
      data-testid="build-protection"
      className="scroll-mt-28 bg-white py-10 lg:py-14">
      <div className="container mx-auto flex flex-col gap-10 px-4 lg:gap-14">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex max-w-lg flex-col gap-2">
            {block.eyebrow ? (
              <Text
                variant="preHeaderSm"
                component="p"
                className="text-adt-blue uppercase">
                {block.eyebrow}
              </Text>
            ) : null}
            {block.title ? (
              <Text
                variant="h3"
                component="h2"
                className="text-greyscale-headline">
                {block.title}
              </Text>
            ) : null}
            {block.subtitle ? (
              <Text
                variant="body1"
                component="p"
                className="text-greyscale-body max-w-lg whitespace-pre-line">
                {block.subtitle}
              </Text>
            ) : null}
          </div>
          <div
            role="tablist"
            aria-label={block.title || "Build protection"}
            id={tabListId}
            tabIndex={-1}
            onKeyDown={onTabsKeyDown}
            className="flex max-w-full gap-6 overflow-x-auto px-1 py-1 outline-none">
            {tabs.map((tab) => {
              const isSelected = tab.id === selected;
              return (
                <button
                  key={tab.id}
                  ref={(node) => {
                    tabRefs.current[tab.id] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`${sectionId}-tab-${tab.id}`}
                  aria-selected={isSelected}
                  aria-controls={`${sectionId}-panel-${tab.id}`}
                  tabIndex={isSelected ? 0 : -1}
                  data-testid={`build-protection-tab-${tab.id}`}
                  onClick={() => setSelected(tab.id)}
                  className={
                    isSelected
                      ? "border-secondary text-secondary focus-visible:ring-secondary min-h-11 shrink-0 border-b-[3px] px-1 pb-2.5 font-bold whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none"
                      : "text-greyscale-body focus-visible:ring-secondary min-h-11 shrink-0 border-b-[3px] border-transparent px-1 pb-2.5 font-semibold whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none"
                  }>
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <PromotionBanner banner={block.promotionBanner} />

        <div
          role="tabpanel"
          id={`${sectionId}-panel-${selected}`}
          aria-labelledby={`${sectionId}-tab-${selected}`}
          data-testid="build-protection-panel">
          <TabPanel block={block} selected={selected} />
        </div>

        {selected === "buildMySystem" || selected === "monitoring" ? null : (
          <>
            <BuildProtectionLegal value={block.legalDisclaimer} />
            <TrustBar reasons={block.trustReasons} />
          </>
        )}
      </div>
    </section>
  );
}

function TabPanel({
  block,
  selected,
}: {
  block: BuildProtectionBlock;
  selected: BuildProtectionTabId;
}) {
  if (selected === "buildMySystem") {
    return (
      <BuildProtectionSystemOverview
        steps={block.systemSteps}
        essentials={block.essentials}
        legalDisclaimer={block.legalDisclaimer}
      />
    );
  }

  if (selected === "securityKits" || selected === "cameras") {
    const items = selected === "securityKits" ? block.kits : block.cameras;
    return (
      <ProductCards items={items} viewDetailsLabel={block.viewDetailsLabel} />
    );
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="flex w-full flex-col items-stretch gap-4 md:flex-row">
        {block.monitoringPlans?.map((plan) => (
          <MonitoringPlanCard
            key={plan._key}
            plan={plan}
            fullWidth
            theme={block.monitoringCardTheme}
          />
        ))}
      </div>
      <BuildProtectionCta
        variant="outline"
        label={block.comparePlansLabel}
        link={block.comparePlansLink}
        testId="build-protection-compare-plans"
      />
    </div>
  );
}

function ProductCards({
  items,
  viewDetailsLabel,
}: {
  items?: BuildProtectionProductItem[] | null;
  viewDetailsLabel?: string | null;
}) {
  const cards = (items ?? []).filter((item) => item.product?.name);
  if (!cards.length) return null;

  const renderCards = () =>
    cards.map((item, index) => (
      <BuildProtectionProductCard
        key={item._key}
        item={item}
        index={index}
        viewDetailsLabel={viewDetailsLabel}
      />
    ));

  return (
    <>
      <div className="hidden lg:grid lg:grid-cols-4 lg:items-stretch lg:gap-5">
        {renderCards()}
      </div>
      <CarouselCentered
        className="lg:hidden"
        mobileSlidesPerView={1}
        desktopSlidesPerView={1}
        centeredSlides={false}
        initialSlide={0}
        equalSlideHeights
        showNavigation>
        {renderCards()}
      </CarouselCentered>
    </>
  );
}

function PromotionBanner({
  banner,
}: {
  banner?: BuildProtectionBlock["promotionBanner"];
}) {
  if (!banner?.promotion) return null;

  return (
    <aside
      data-testid="build-protection-promo"
      className={`flex flex-col gap-4 rounded-2xl bg-white px-5 py-4 lg:flex-row lg:items-center lg:justify-between lg:px-6 ${BUILD_PROTECTION_SURFACE_SHADOW}`}>
      <div className="flex min-w-0 flex-col gap-1">
        {banner.eyebrow ? (
          <Text
            variant="preHeaderSm"
            component="p"
            className="text-adt-blue uppercase">
            {banner.eyebrow}
          </Text>
        ) : null}
        {banner.promotion.callout ? (
          <div className="text-greyscale-headline text-xl font-bold">
            <RichText>{banner.promotion.callout}</RichText>
          </div>
        ) : banner.promotion.name ? (
          <Text variant="h6" component="p" className="text-greyscale-headline">
            {banner.promotion.name}
          </Text>
        ) : null}
        <PromotionLegal legalCallout={banner.promotion.legalCallout} />
      </div>
      <BuildProtectionCta
        variant="amber"
        label={banner.ctaLabel}
        link={banner.ctaLink}
        testId="build-protection-promo-cta"
        className="w-full shrink-0 lg:w-auto"
      />
    </aside>
  );
}

function PromotionLegal({
  legalCallout,
}: {
  legalCallout?: RichTextType | null;
}) {
  if (
    !legalCallout ||
    (Array.isArray(legalCallout) && legalCallout.length === 0)
  ) {
    return null;
  }

  return (
    <div data-testid="build-protection-promo-legal">
      <RichText
        interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}
        blockComponents={{
          normal: ({ children }) => (
            <Text variant="legal" component="p" className="text-greyscale-body">
              {children}
            </Text>
          ),
          legal: ({ children }) => (
            <Text variant="legal" component="p" className="text-greyscale-body">
              {children}
            </Text>
          ),
        }}>
        {legalCallout}
      </RichText>
    </div>
  );
}

function TrustBar({
  reasons,
}: {
  reasons?: BuildProtectionBlock["trustReasons"];
}) {
  if (!reasons?.length) return null;

  return (
    <ul
      data-testid="build-protection-trust"
      className={`bg-primary-100 divide-secondary/10 flex flex-col divide-y rounded-2xl px-6 py-2 md:flex-row md:divide-x md:divide-y-0 md:px-4 ${BUILD_PROTECTION_SURFACE_SHADOW}`}>
      {reasons.map((reason) => (
        <li key={reason._key} className="py-6 md:flex-1 md:px-6 md:py-8">
          <ReasonsToBuyItem
            layout="inline"
            title={reason.title}
            description={reason.description}
            icon={reason.icon}
            className="bg-transparent p-0"
          />
        </li>
      ))}
    </ul>
  );
}

type BuildProtectionTab = {
  id: BuildProtectionTabId;
  label: string;
};

function availableTabs(block: BuildProtectionBlock): BuildProtectionTab[] {
  const tabs: BuildProtectionTab[] = [];
  if (block.systemSteps?.length || block.essentials?.title) {
    tabs.push({
      id: "buildMySystem",
      label: block.buildMySystemLabel || "Build my system",
    });
  }
  if (block.kits?.some((item) => item.product?.name)) {
    tabs.push({
      id: "securityKits",
      label: block.securityKitsLabel || "Security kits",
    });
  }
  if (block.cameras?.some((item) => item.product?.name)) {
    tabs.push({
      id: "cameras",
      label: block.camerasLabel || "Cameras",
    });
  }
  if (block.monitoringPlans?.length) {
    tabs.push({
      id: "monitoring",
      label: block.monitoringLabel || "Monitoring",
    });
  }
  return tabs;
}

function resolveDefaultTab(
  defaultTab: BuildProtectionBlock["defaultTab"],
  tabs: BuildProtectionTab[],
): BuildProtectionTabId | null {
  if (
    isBuildProtectionTabId(defaultTab) &&
    tabs.some((tab) => tab.id === defaultTab)
  ) {
    return defaultTab;
  }
  return tabs[0]?.id ?? null;
}
